import { useState, type ReactNode } from 'react';
import {
  Alert,
  AttachmentField,
  AttachmentGallery,
  Badge,
  Button,
  CopyButton,
  DeskModal,
  DateField,
  DetailSection,
  Fact,
  Icon,
  PersonMark,
  RelatedGroup,
  RelatedItem,
  SearchSelect,
  TextArea,
  Modal,
  ModalActions,
  linkTextClasses,
  type AttachmentItem,
  type IconName,
} from '@evogard/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import type {
  FinanceExpenseActivity,
  FinanceExpenseDetail,
  FinanceExpenseFile,
  FinanceExpenseOrigin,
} from '@evogard/contract';
import {
  EXPENSE_FINANCE_ACT_LABELS,
  EXPENSE_PAYMENT_METHOD_LABELS,
  EXPENSE_REQUEST_STAGE_LABELS,
  EXPENSE_SOURCE_LABELS,
  EXPENSE_STAGE_LABELS,
  EXPENSE_STAGE_TONES,
  assistanceService,
  contaAzulCatalogOf,
  contaAzulCategoriesFor,
  formatDate,
  formatDueDate,
  formatDocument,
  formatInstant,
  pixKeyTypeOf,
  type ExpenseFinanceAct,
} from '@evogard/domain';
import {
  ACCOUNT_TYPE_LABELS,
  PIX_TYPE_LABELS,
  bankLabel,
} from '../../../components/beneficiary/labels.js';
import { LabelsPicker } from '../../../components/labels/LabelsPicker.js';
import {
  ActivityRecordButtons,
  HoldDialog,
  type ActivityRecord,
} from '../../../components/activity/ActivityRecords.js';
import {
  ExpenseNumber,
  beneficiaryOf,
  expenseAmount,
  requesterOf,
} from '../../../components/expense/SupervisorExpenseBoard.js';
import { can, useSessionContext } from '../../../lib/session.js';
import { FinanceBeneficiaryDrawer } from './FinanceBeneficiaryDrawer.js';
import { FinanceClaimDrawer } from './FinanceClaimDrawer.js';
import {
  financeFileUrl,
  useFinanceActivity,
  useFinanceAssigneeOptions,
  useFinanceBeneficiary,
  useFinanceDeskMutations,
  useFinanceExpense,
  useFinanceFiles,
  useFinanceLabelOptions,
} from './useFinanceDesk.js';

type Tab = 'formulario' | 'atividade' | 'anexos';
/** A gaveta aberta ao lado da mesa — uma de cada vez. */
type Drawer = 'beneficiario' | 'sinistro' | null;

/** "Sinistro · Reparo", "Assistência 24h". */
export function originOf(e: { sourceType: string; stage: string | null }): string {
  const source =
    EXPENSE_SOURCE_LABELS[e.sourceType as keyof typeof EXPENSE_SOURCE_LABELS];
  return e.stage
    ? `${source} · ${EXPENSE_REQUEST_STAGE_LABELS[e.stage as keyof typeof EXPENSE_REQUEST_STAGE_LABELS]}`
    : source;
}

/**
 * A mesa da despesa no Financeiro (27/10/2026) — a anatomia do card do
 * sinistro e de Periféricos (`DeskModal`), a pedido do Paulo:
 *
 * - **no alto**: o responsável (quem decide no Financeiro), o vencimento da
 *   despesa e as etiquetas dos setores de quem olha;
 * - **à esquerda**: o resumo — origem, valor, beneficiário, e o Conta Azul;
 * - **no miolo**: Formulário (o pedido, e na Aprovação Financeiro a
 *   categoria, o centro de custo e a conta de pagamento), Atividade (o
 *   caminho e os comentários) e Anexos (os três da despesa e os do Financeiro);
 * - **à direita**: o movimento — os atos do Financeiro como destinos, e o
 *   motivo quando o ato pede.
 *
 * Etiqueta, responsável, comentário e anexo gravam na hora. Quem só vê
 * (`finance.expenses:ver`) vê tudo, sem os controles.
 */
export function FinanceExpenseDesk({ id, onClose }: { id: number; onClose: () => void }) {
  const { data: context } = useSessionContext();
  const decides = can(context, 'finance.expenses', 'decidir');
  const slug = context?.currentTenant.slug ?? '';
  const detail = useFinanceExpense(id);
  const activity = useFinanceActivity(id);
  const files = useFinanceFiles(id);
  const labelOptions = useFinanceLabelOptions(decides);
  const mutations = useFinanceDeskMutations(id);
  const [tab, setTab] = useState<Tab>('formulario');
  const [refusing, setRefusing] = useState(false);
  const [holding, setHolding] = useState(false);
  const [running, setRunning] = useState<ExpenseFinanceAct | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [costCenter, setCostCenter] = useState<string | null>(null);
  const [paymentAccount, setPaymentAccount] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<Drawer>(null);
  const e = detail.data;

  const count = (n: number | undefined) =>
    n === undefined ? null : (
      <span className="ml-1.5 rounded-pilula bg-superficie-suave px-1.5 text-xs tabular-nums text-texto-suave">
        {n}
      </span>
    );

  /* O que o Formulário tem — o rascunho do Financeiro, ou o que já foi gravado. */
  const chosen = {
    category: category ?? e?.category ?? '',
    costCenter: costCenter ?? e?.costCenter ?? '',
    paymentAccount: paymentAccount ?? e?.paymentAccount ?? '',
    dueDate: dueDate ?? e?.dueDate ?? '',
  };

  /** Um ato do rodapé. O que pede motivo chega com ele; autorizar leva o Formulário. */
  const run = async (act: ExpenseFinanceAct, reason: string | null = null) => {
    if (!e) return;
    if (
      act === 'autorizar' &&
      (!chosen.category || !chosen.costCenter || !chosen.paymentAccount)
    ) {
      setError(
        'Escolha a categoria, o centro de custo e a conta de pagamento na Aprovação Financeiro.',
      );
      setTab('formulario');
      return;
    }
    if (act === 'autorizar' && !chosen.dueDate) {
      setError('Preencha o vencimento na Aprovação Financeiro.');
      setTab('formulario');
      return;
    }
    setError(null);
    setRunning(act);
    try {
      await mutations.act.mutateAsync({
        act,
        reason,
        ...(act === 'autorizar' && {
          category: chosen.category,
          costCenter: chosen.costCenter,
          paymentAccount: chosen.paymentAccount,
          /* Só a despesa que chegou sem vencimento — o lote da assistência — o leva. */
          ...(e.dueDate === null && { dueDate: chosen.dueDate }),
        }),
      });
      setRefusing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível concluir.');
      throw err;
    } finally {
      setRunning(null);
    }
  };

  return (
    <DeskModal
      open
      onOpenChange={(open) => !open && onClose()}
      title={
        e ? (
          <span className="inline-flex flex-wrap items-center gap-2">
            <ExpenseNumber id={e.id} className="text-sm" />
            {beneficiaryOf(e)}
          </span>
        ) : (
          'Carregando…'
        )
      }
      descricao={e ? (e.description ?? originOf(e)) : undefined}
      belowTitle={
        e && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <AssigneePicker
              expense={e}
              enabled={decides}
              onChange={(v) => mutations.setAssignee.mutate(v)}
              saving={mutations.setAssignee.isPending}
            />
            <span
              className="text-sm text-texto-suave tabular-nums"
              title="O vencimento da despesa"
            >
              {e.dueDate ? `Vence em ${formatDate(e.dueDate)}` : 'Sem vencimento'}
            </span>
            <LabelsPicker
              labels={e.labels}
              editable={decides}
              save={mutations.setLabels}
              options={labelOptions}
            />
          </div>
        )
      }
      leftLabel="A despesa"
      left={
        e ? (
          <Summary
            expense={e}
            onOpenBeneficiary={() => setDrawer('beneficiario')}
            onOpenClaim={() => setDrawer('sinistro')}
          />
        ) : null
      }
      drawer={
        drawer === 'beneficiario' ? (
          <FinanceBeneficiaryDrawer expenseId={id} onClose={() => setDrawer(null)} />
        ) : drawer === 'sinistro' ? (
          <FinanceClaimDrawer expenseId={id} onClose={() => setDrawer(null)} />
        ) : undefined
      }
      onCloseDrawer={() => setDrawer(null)}
      middleLabel="Conteúdo da despesa"
      footer={
        e && decides && e.acts.length > 0 ? (
          <DeskActions
            expense={e}
            error={error}
            running={running}
            onRun={(act) => void run(act).catch(() => undefined)}
            onRefuse={() => {
              setError(null);
              setRefusing(true);
            }}
            onHold={() => {
              setError(null);
              setHolding(true);
            }}
          />
        ) : undefined
      }
    >
      {detail.isError ? (
        <Alert tone="erro">{detail.error.message}</Alert>
      ) : !e ? (
        <p className="text-sm text-texto-suave">Carregando a despesa…</p>
      ) : (
        <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
          <TabsList>
            <TabsTrigger value="formulario">Formulário</TabsTrigger>
            <TabsTrigger value="atividade">
              Atividade{count(activity.data?.length)}
            </TabsTrigger>
            <TabsTrigger value="anexos">Anexos{count(files.data?.length)}</TabsTrigger>
          </TabsList>
          <TabsContent value="formulario" className="mt-4 flex flex-col gap-4">
            <FormTab
              expense={e}
              slug={slug}
              editable={decides && e.status === 'aprovacao_financeiro'}
              chosen={chosen}
              onCategory={setCategory}
              onCostCenter={setCostCenter}
              onPaymentAccount={setPaymentAccount}
              onDueDate={setDueDate}
              files={files.data}
              canAttach={decides}
              mutations={mutations}
            />
          </TabsContent>
          <TabsContent value="atividade" className="mt-4">
            <ActivityTab
              expense={e}
              onHold={() => setHolding(true)}
              items={activity.data}
              error={activity.error}
              canComment={decides}
              mutations={mutations}
              me={context?.user.id ?? null}
            />
          </TabsContent>
          <TabsContent value="anexos" className="mt-4">
            <FilesTab
              expenseId={e.id}
              files={files.data}
              error={files.error}
              canAttach={decides}
              mutations={mutations}
            />
          </TabsContent>
        </Tabs>
      )}
      {holding && e && (
        <HoldDialog
          type="pendencia"
          description={
            e.departmentApproverName
              ? `A despesa vai para Pendente e volta para ${e.departmentApproverName}, que a aprovou: ele recebe o aviso com esta mensagem e resolve.`
              : 'A despesa vai para Pendente e volta para o supervisor do departamento, que resolve.'
          }
          submit={({ text: why }) =>
            mutations.act.mutateAsync({ act: 'pendenciar', reason: why })
          }
          onClose={() => setHolding(false)}
        />
      )}
      {refusing && e && (
        <RefuseDialog
          busy={running === 'recusar'}
          onCancel={() => setRefusing(false)}
          onConfirm={(why) => run('recusar', why)}
        />
      )}
    </DeskModal>
  );
}

function AssigneePicker({
  expense,
  enabled,
  onChange,
  saving,
}: {
  expense: FinanceExpenseDetail;
  enabled: boolean;
  onChange: (userId: string | null) => void;
  saving: boolean;
}) {
  const options = useFinanceAssigneeOptions(enabled);
  const assignee = expense.assignee;
  if (!enabled) {
    return (
      <PersonMark
        name={assignee?.name ?? null}
        emptyLabel="Sem responsável"
        className="max-w-56 text-sm"
      />
    );
  }
  /* Quem já é responsável e deixou de poder decidir continua aparecendo. */
  const list = options.data ?? [];
  const choices =
    assignee && !list.some((x) => x.id === assignee.id) ? [assignee, ...list] : list;
  return (
    <SearchSelect
      label="Responsável"
      options={choices.map((x) => ({ value: x.id, label: x.name }))}
      value={assignee?.id ?? ''}
      onChange={(v) => onChange(v === '' ? null : v)}
      searchPlaceholder="Buscar pessoa"
      emptyMessage="Ninguém que decide no Financeiro com esse nome."
      loading={options.isLoading}
      clearable
      sortable
      compact={{
        icon: 'pessoa',
        filled: assignee !== null,
        text: assignee?.name,
        title: assignee ? `Responsável: ${assignee.name}` : 'Escolher o responsável',
        loading: saving,
      }}
    />
  );
}

/** A coluna da esquerda: o que se consulta a cada passo. */
function Summary({
  expense: e,
  onOpenBeneficiary,
  onOpenClaim,
}: {
  expense: FinanceExpenseDetail;
  onOpenBeneficiary: () => void;
  onOpenClaim: () => void;
}) {
  return (
    <div className="flex flex-col gap-4 px-1">
      <RelatedGroup title="Situação">
        <div className="flex flex-wrap gap-1.5 px-2">
          <Badge tone={EXPENSE_STAGE_TONES[e.status]}>
            {EXPENSE_STAGE_LABELS[e.status]}
          </Badge>
          {e.alreadyPaid && <Badge tone="sucesso">Já pago</Badge>}
          {!e.hasTaxInvoice && <Badge tone="alerta">NF pendente</Badge>}
        </div>
      </RelatedGroup>
      <dl className="flex flex-col gap-3 px-2 text-sm">
        <Fact label="Valor" icon="dinheiro">
          <span className="tabular-nums">{expenseAmount(e.amount)}</span>
        </Fact>
        <Fact label="Vencimento" icon="calendario">
          <span className="tabular-nums">{formatDueDate(e.dueDate)}</span>
        </Fact>
        <Fact label="Origem" icon="bandeira">
          {originOf(e)}
        </Fact>
        <Fact label="Beneficiário" icon="pessoa">
          <button
            type="button"
            onClick={onOpenBeneficiary}
            title="Ver o beneficiário: dados, contatos e contas"
            className={linkTextClasses('justify-start text-left font-semibold')}
          >
            {beneficiaryOf(e)}
          </button>
          <span className="block text-xs text-texto-suave">Ver dados e contas</span>
        </Fact>
        <Fact label="Solicitante" icon="cracha">
          <span className="font-semibold">{requesterOf(e)}</span>
          <span className="block text-xs text-texto-suave">
            {formatInstant(e.createdAt)}
          </span>
        </Fact>
        <Fact label="Aprovada pelo líder" icon="confirmado">
          {e.departmentApproverName ? (
            <>
              <span className="font-semibold">{e.departmentApproverName}</span>
              {e.departmentApprovedAt && (
                <span className="block text-xs text-texto-suave">
                  {formatInstant(e.departmentApprovedAt)}
                </span>
              )}
            </>
          ) : (
            <span className="text-texto-suave">Não registrado</span>
          )}
        </Fact>
      </dl>
      <OriginGroup origin={e.origin} onOpenClaim={onOpenClaim} />
      <ContaAzulState expense={e} />
    </div>
  );
}

/**
 * De onde a despesa veio (28/10/2026): o card do sinistro, o acionamento de
 * Periféricos ou os acionamentos da assistência que o acerto paga. Cada um
 * abre em outra aba, para o Financeiro não perder a mesa; o link só aparece
 * para quem pode ver o destino — os outros leem o código.
 */
function OriginGroup({
  origin,
  onOpenClaim,
}: {
  origin: FinanceExpenseOrigin | null;
  /** O sinistro abre na gaveta, ao lado da mesa. */
  onOpenClaim: () => void;
}) {
  const { data: context } = useSessionContext();
  /* O roteador não tem `basepath`: o caminho da rota é o endereço. */
  const openTab = (href: string) => window.open(href, '_blank', 'noopener');
  if (origin === null) {
    return (
      <RelatedGroup
        title="Pedido de origem"
        empty="O pedido que originou esta despesa não foi achado."
      />
    );
  }
  const visual = <Icon nome="documento" size={20} className="mt-0.5 text-texto-suave" />;
  if (origin.kind === 'sinistro') {
    return (
      <RelatedGroup title="Pedido de origem">
        <RelatedItem
          name={`Sinistro #${origin.claimId}`}
          visual={visual}
          subtitle={EXPENSE_REQUEST_STAGE_LABELS[origin.stage]}
          onOpen={onOpenClaim}
          openLabel={`Ver o sinistro #${origin.claimId} ao lado`}
        />
      </RelatedGroup>
    );
  }
  if (origin.kind === 'periferico') {
    const canOpen = can(context, 'claims.peripherals', 'ver');
    return (
      <RelatedGroup title="Pedido de origem">
        <RelatedItem
          name={`Periférico #${origin.peripheralId}`}
          visual={visual}
          subtitle="Acionamento de Periféricos"
          onOpen={
            canOpen
              ? () => openTab(`/claims/peripherals?open=${origin.peripheralId}`)
              : undefined
          }
          openLabel={`Abrir o acionamento #${origin.peripheralId} em outra aba`}
        />
      </RelatedGroup>
    );
  }
  if (origin.kind === 'veiculo') {
    return (
      <RelatedGroup title="Pedido de origem">
        <RelatedItem
          name={`Veículo ${origin.vehicleCode}`}
          visual={visual}
          subtitle="Lançada no sinistro sem card"
        />
      </RelatedGroup>
    );
  }
  const canOpen = can(context, 'assistance.dispatches', 'ver');
  const provider = origin.providerName ?? 'prestador sem nome';
  const settlement =
    origin.settlementKind === 'a_vista'
      ? `Pago à vista · ${provider}`
      : origin.competence
        ? `Fechamento de ${origin.competence.slice(5, 7)}/${origin.competence.slice(0, 4)} · ${provider}`
        : `Lote mensal · ${provider}`;
  return (
    <RelatedGroup
      title={`Acionamentos · ${settlement}`}
      count={origin.dispatches.length}
      empty="O acerto não tem acionamento ligado."
    >
      {origin.dispatches.map((d) => (
        <RelatedItem
          key={d.id}
          name={d.code}
          visual={visual}
          subtitle={[
            assistanceService(d.serviceType).label,
            d.executedAt ? formatInstant(d.executedAt) : null,
            d.amount ? expenseAmount(d.amount) : null,
          ]
            .filter(Boolean)
            .join(' · ')}
          onOpen={
            canOpen ? () => openTab(`/assistance/dispatches?open=${d.id}`) : undefined
          }
          openLabel={`Abrir o acionamento ${d.code} em outra aba`}
        />
      ))}
    </RelatedGroup>
  );
}

function ContaAzulState({ expense: e }: { expense: FinanceExpenseDetail }) {
  if (e.contaAzul.state === 'nao_enviada') return null;
  const tone =
    e.contaAzul.state === 'lancada'
      ? 'sucesso'
      : e.contaAzul.state === 'recusada'
        ? 'alerta'
        : 'informacao';
  const title =
    e.contaAzul.state === 'lancada'
      ? 'Lançada no Conta Azul'
      : e.contaAzul.state === 'recusada'
        ? 'Não foi para o Conta Azul'
        : 'Enviando ao Conta Azul';
  return (
    <Alert tone={tone} title={title}>
      {e.contaAzul.state === 'lancada'
        ? 'A baixa dada lá chega aqui pelo botão Atualizar do Conta Azul, no alto do quadro.'
        : (e.contaAzul.message ?? 'A fila está criando a conta a pagar no Conta Azul.')}
    </Alert>
  );
}

/**
 * O Formulário (refeito em 28/10/2026, pedido do Paulo): o pedido em campos
 * com ícone, os dados bancários de quem recebe — no lugar do resumo financeiro
 * do Pipefy, que repetia o que os campos já dizem —, os documentos da despesa
 * (a NF-e e o comprovante se anexam aqui mesmo, quando faltam) e, na Aprovação
 * Financeiro, o que o Financeiro escolhe.
 */
function FormTab({
  expense: e,
  slug,
  editable,
  chosen,
  onCategory,
  onCostCenter,
  onPaymentAccount,
  onDueDate,
  files,
  canAttach,
  mutations,
}: {
  expense: FinanceExpenseDetail;
  slug: string;
  editable: boolean;
  chosen: {
    category: string;
    costCenter: string;
    paymentAccount: string;
    dueDate: string;
  };
  onCategory: (v: string) => void;
  onCostCenter: (v: string) => void;
  onPaymentAccount: (v: string) => void;
  onDueDate: (v: string) => void;
  files: FinanceExpenseFile[] | undefined;
  canAttach: boolean;
  mutations: Mutations;
}) {
  const catalog = contaAzulCatalogOf(slug);
  const categories = contaAzulCategoriesFor(
    slug,
    e.sourceType === 'assistencia' ? 'assistencia' : (e.stage ?? 'aviso'),
  );
  const nameOf = (
    list: readonly { id: string; name: string }[] | undefined,
    id: string | null,
  ) => (id ? (list?.find((x) => x.id === id)?.name ?? `id ${id} (fora da lista)`) : '—');
  return (
    <>
      <DetailSection title="O pedido" icon="documento">
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
          <Info
            label="Valor"
            icon="dinheiro"
            value={<span className="tabular-nums">{expenseAmount(e.amount)}</span>}
          />
          <Info
            label="Forma de pagamento"
            icon="cartao"
            value={EXPENSE_PAYMENT_METHOD_LABELS[e.paymentMethod]}
          />
          <Info
            label="Vencimento"
            icon="calendario"
            value={<span className="tabular-nums">{formatDueDate(e.dueDate)}</span>}
          />
          <Info
            label="Competência"
            icon="calendario"
            placeholder="Não informada"
            value={
              e.competencyDate ? (
                <span className="tabular-nums">{formatDate(e.competencyDate)}</span>
              ) : null
            }
          />
          <Info label="Beneficiário" icon="pessoa" value={beneficiaryOf(e)} />
          <Info label="Origem" icon="bandeira" value={originOf(e)} />
          <Info
            label="Descrição"
            icon="documento"
            wide
            placeholder="Sem descrição"
            value={e.description}
          />
          {/* O que o operador escreveu ao lançar — aparece mesmo vazio, para o
              Financeiro saber que não há, e não achar que a tela esqueceu. */}
          <Info
            label="Observações de quem pediu"
            icon="livro"
            wide
            placeholder="O operador não escreveu observação."
            value={
              e.notes ? <span className="whitespace-pre-wrap">{e.notes}</span> : null
            }
          />
          {e.pendingReason && (
            <Info
              label="Pendência"
              icon="atencao"
              wide
              value={<span className="whitespace-pre-wrap">{e.pendingReason}</span>}
            />
          )}
          {e.refusalReason && (
            <Info
              label="Motivo da recusa"
              icon="atencao"
              wide
              value={<span className="whitespace-pre-wrap">{e.refusalReason}</span>}
            />
          )}
        </dl>
      </DetailSection>
      <BankSection expenseId={e.id} />
      <DocumentsSection
        expenseId={e.id}
        files={files}
        canAttach={canAttach}
        mutations={mutations}
      />
      <DetailSection title="Aprovação Financeiro" icon="confirmado">
        {catalog === null ? (
          <Alert tone="informacao">
            Esta base ainda não tem as listas do Conta Azul na plataforma, e sem elas não
            há como autorizar.
          </Alert>
        ) : editable ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Na assistência a categoria é a do serviço de cada acionamento, e o
                Conta Azul recebe o valor dividido por elas (30/10/2026). */}
            {e.sourceType === 'assistencia' ? (
              <Fact label="Categoria" icon="etiqueta">
                Pelo serviço de cada acionamento
                <span className="block text-xs text-texto-suave">
                  O valor vai ao Conta Azul dividido pela categoria de cada um.
                </span>
              </Fact>
            ) : (
              <SearchSelect
                label="Categoria"
                required
                options={categories.map((c) => ({ value: c.id, label: c.name }))}
                value={chosen.category}
                onChange={onCategory}
                help="As categorias do departamento que pediu, e as gerais."
              />
            )}
            {e.dueDate === null && (
              <DateField
                label="Vencimento"
                required
                value={chosen.dueDate}
                onChange={onDueDate}
                help="O lote da assistência chega sem vencimento: quem preenche é o Financeiro."
              />
            )}
            <SearchSelect
              label="Centro de custo"
              required
              options={catalog.costCenters.map((c) => ({ value: c.id, label: c.name }))}
              value={chosen.costCenter}
              onChange={onCostCenter}
            />
            <SearchSelect
              label="Conta de pagamento"
              required
              options={catalog.paymentAccounts.map((c) => ({
                value: c.id,
                label: c.name,
              }))}
              value={chosen.paymentAccount}
              onChange={onPaymentAccount}
            />
            <p className="self-end text-xs text-texto-suave sm:pb-2.5">
              O que você escolhe aqui vai com o Autorizar, no rodapé.
            </p>
          </div>
        ) : (
          <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
            <Fact label="Categoria" icon="etiqueta">
              {nameOf(catalog.categories, e.category)}
            </Fact>
            <Fact label="Centro de custo" icon="grade">
              {nameOf(catalog.costCenters, e.costCenter)}
            </Fact>
            <Fact label="Conta de pagamento" icon="dinheiro">
              {nameOf(catalog.paymentAccounts, e.paymentAccount)}
            </Fact>
          </dl>
        )}
      </DetailSection>
    </>
  );
}

type Mutations = ReturnType<typeof useFinanceDeskMutations>;

/**
 * Um dado do Formulário (28/10/2026, pedido do Paulo): `<ícone> Rótulo` em
 * cima e a informação embaixo, sem caixa — o `Fact` da casa. O vazio diz o
 * que falta, e não some.
 */
function Info({
  label,
  icon,
  value,
  placeholder = 'Não informado',
  wide = false,
}: {
  label: string;
  icon: IconName;
  value: ReactNode;
  placeholder?: string;
  wide?: boolean;
}) {
  const empty = value === null || value === undefined || value === '';
  return (
    <Fact label={label} icon={icon} wide={wide}>
      {empty ? (
        <span className="font-normal text-texto-tenue">{placeholder}</span>
      ) : (
        value
      )}
    </Fact>
  );
}

/**
 * Os dados bancários de quem recebe, na conta DESTA despesa — o que o resumo
 * financeiro do Pipefy dizia em texto, agora em campos (28/10/2026). Vem da
 * mesma consulta da gaveta do beneficiário: mascarado para quem não vê dados
 * bancários, e na auditoria para quem vê.
 */
function BankSection({ expenseId }: { expenseId: number }) {
  const query = useFinanceBeneficiary(expenseId);
  const account = query.data?.accounts.find((a) => a.current) ?? null;
  const pixType = account && !query.data?.masked ? pixKeyTypeOf(account.pixKey) : null;
  return (
    <DetailSection title="Dados bancários" icon="dinheiro">
      {query.isError ? (
        <Alert tone="erro">{query.error.message}</Alert>
      ) : !query.data ? (
        <p className="text-sm text-texto-suave">Carregando os dados bancários…</p>
      ) : !account ? (
        <Alert tone="alerta">A conta desta despesa não foi achada no cadastro.</Alert>
      ) : (
        <>
          {query.data.masked && (
            <Alert tone="informacao">
              Agência, conta e chave PIX saem mascaradas: ver os dados bancários inteiros
              pede a permissão de ver dados bancários dos beneficiários.
            </Alert>
          )}
          <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
            <Info
              label="Banco"
              icon="cracha"
              placeholder="Não informado"
              value={bankLabel(account.bankCode, account.bankName)}
              wide
            />
            <Info
              label="Agência"
              icon="codigo-barras"
              placeholder="Não informada"
              value={
                account.agency ? (
                  <span className="tabular-nums">{account.agency}</span>
                ) : null
              }
            />
            <Info
              label="Conta"
              icon="codigo-barras"
              placeholder="Não informada"
              value={
                account.accountNumber ? (
                  <span className="tabular-nums">
                    {account.accountNumber}
                    {account.accountType
                      ? ` · ${ACCOUNT_TYPE_LABELS[account.accountType]}`
                      : ''}
                  </span>
                ) : null
              }
            />
            <Info
              label={pixType ? `Chave PIX · ${PIX_TYPE_LABELS[pixType]}` : 'Chave PIX'}
              icon="pix"
              placeholder="Não informada"
              wide
              value={
                account.pixKey ? (
                  <span className="inline-flex flex-wrap items-center gap-2">
                    <span className="break-all">{account.pixKey}</span>
                    {!query.data.masked && (
                      <CopyButton text={account.pixKey} label="Copiar" />
                    )}
                  </span>
                ) : null
              }
            />
            <Info
              label="Titular"
              icon="pessoa"
              placeholder="O próprio beneficiário"
              value={account.accountHolderName}
            />
            <Info
              label="CPF/CNPJ"
              icon="documento"
              placeholder="Não informado"
              value={
                account.document ? (
                  <span className="tabular-nums">{formatDocument(account.document)}</span>
                ) : null
              }
            />
          </dl>
        </>
      )}
    </DetailSection>
  );
}

/** Um arquivo da despesa como a grade de anexos da casa o desenha, com prévia. */
function fileItem(
  expenseId: number,
  f: FinanceExpenseFile,
  label?: string,
): AttachmentItem {
  return {
    key: f.key,
    nome: f.fileName,
    typeMime: f.mimeType ?? 'application/octet-stream',
    sizeBytes: f.sizeBytes ?? 0,
    detail:
      [label, f.createdByName, f.createdAt ? formatInstant(f.createdAt) : null]
        .filter(Boolean)
        .join(' · ') || undefined,
    resolveUrl: () => financeFileUrl(expenseId, f.key),
  };
}

/**
 * Os documentos da despesa no Formulário (28/10/2026): o documento a pagar, que
 * vem de quem pediu, e a NF-e e o comprovante — que o Financeiro anexa aqui
 * mesmo quando faltam. Anexado, o arquivo passa a ser a nota ou o comprovante
 * da despesa; trocar um que já existe não se faz por aqui.
 */
function DocumentsSection({
  expenseId,
  files,
  canAttach,
  mutations,
}: {
  expenseId: number;
  files: FinanceExpenseFile[] | undefined;
  canAttach: boolean;
  mutations: Mutations;
}) {
  const slot = (name: FinanceExpenseFile['slot']) =>
    files?.find((f) => f.slot === name) ?? null;
  const sending = mutations.attach.isPending
    ? mutations.attach.variables?.kind
    : undefined;
  return (
    <DetailSection title="Documentos" icon="clipe">
      {!files ? (
        <p className="text-sm text-texto-suave">Carregando os documentos…</p>
      ) : (
        <div className="flex flex-col gap-4">
          <DocumentSlot
            label="Documento a pagar"
            expenseId={expenseId}
            file={slot('documento')}
          />
          <DocumentSlot
            label="NF-e"
            expenseId={expenseId}
            file={slot('nota_fiscal')}
            onAdd={
              canAttach
                ? (file) => mutations.attach.mutate({ file, kind: 'nota_fiscal' })
                : undefined
            }
            busy={sending === 'nota_fiscal'}
          />
          <DocumentSlot
            label="Comprovante de pagamento"
            expenseId={expenseId}
            file={slot('comprovante')}
            onAdd={
              canAttach
                ? (file) => mutations.attach.mutate({ file, kind: 'comprovante' })
                : undefined
            }
            busy={sending === 'comprovante'}
          />
          {mutations.attach.isError && (
            <Alert tone="erro">{mutations.attach.error.message}</Alert>
          )}
        </div>
      )}
    </DetailSection>
  );
}

/** Um documento: com prévia quando existe; vazio, o campo de anexar — ou o vazio dito. */
function DocumentSlot({
  label,
  expenseId,
  file,
  onAdd,
  busy = false,
}: {
  label: string;
  expenseId: number;
  file: FinanceExpenseFile | null;
  /** Ausente, o vazio só se lê. */
  onAdd?: (file: File) => void;
  busy?: boolean;
}) {
  if (file) {
    return <AttachmentGallery label={label} items={[fileItem(expenseId, file)]} />;
  }
  if (!onAdd) {
    return (
      <dl>
        <Info label={label} icon="clipe" placeholder="Não anexado." value={null} />
      </dl>
    );
  }
  return (
    <AttachmentField
      label={label}
      max={1}
      items={[]}
      busy={busy}
      disabled={busy}
      onAdd={(picked) => {
        const first = picked[0];
        if (first) onAdd(first);
      }}
      onRemove={() => undefined}
    />
  );
}

/** A Atividade: o compositor no alto, e o caminho e os comentários, do mais novo ao mais velho. */
/** O registro da Atividade do Financeiro: só a pendência — o padrão do sinistro. */
// @valor
const FINANCE_RECORDS: readonly ActivityRecord<'pendencia'>[] = [
  { code: 'pendencia', icon: 'pausa', title: 'Pendência', stops: true },
];

/**
 * A pendência do Financeiro, no alto da Atividade (28/10/2026, pedido do
 * Paulo — o padrão do sinistro): o motivo, quem pendenciou e desde quando. O
 * Financeiro não retoma: quem resolve é o líder que aprovou, e a despesa volta
 * para Aprovação Financeiro quando ele resolver.
 */
function PendingNotice({ expense: e }: { expense: FinanceExpenseDetail }) {
  if (e.status !== 'pendente') return null;
  return (
    <Alert tone="alerta" title="Pendente: com o líder que aprovou">
      {e.pendingReason && <p className="whitespace-pre-wrap">{e.pendingReason}</p>}
      <p className="mt-1 text-xs">
        {[
          e.statusByName ? `Por ${e.statusByName}` : null,
          e.statusSince ? `desde ${formatInstant(e.statusSince)}` : null,
        ]
          .filter(Boolean)
          .join(', ')}
        {e.statusByName || e.statusSince ? '. ' : ''}
        {e.departmentApproverName
          ? `${e.departmentApproverName} foi avisado e resolve; a despesa volta para Aprovação Financeiro quando ele resolver.`
          : 'Quem resolve é o supervisor do departamento; a despesa volta para Aprovação Financeiro quando ele resolver.'}
      </p>
    </Alert>
  );
}

function ActivityTab({
  expense: e,
  onHold,
  items,
  error,
  canComment,
  mutations,
  me,
}: {
  expense: FinanceExpenseDetail;
  /** Abre o diálogo da pendência — o mesmo do rodapé. */
  onHold: () => void;
  items: FinanceExpenseActivity[] | undefined;
  error: Error | null;
  canComment: boolean;
  mutations: Mutations;
  me: string | null;
}) {
  const [text, setText] = useState('');
  const [editing, setEditing] = useState<{ id: number; text: string } | null>(null);
  /* A pendência só existe onde o Financeiro pode pendenciar — na Aprovação Financeiro. */
  const canHold = canComment && e.acts.includes('pendenciar');
  if (error) return <Alert tone="erro">{error.message}</Alert>;
  return (
    <div className="flex flex-col gap-4">
      <PendingNotice expense={e} />
      {canHold && (
        <ActivityRecordButtons
          records={FINANCE_RECORDS}
          held={e.status === 'pendente'}
          heldTitle="A despesa já está pendente: o líder resolve."
          onOpen={onHold}
        />
      )}
      {canComment && (
        <div className="flex flex-col gap-2">
          <TextArea
            label="Comentar"
            placeholder="Escreva um comentário sobre a despesa"
            value={text}
            onChange={(ev) => setText(ev.target.value)}
            maxLength={4000}
            rows={2}
          />
          {mutations.comment.isError && (
            <Alert tone="erro">{mutations.comment.error.message}</Alert>
          )}
          <div className="flex justify-end">
            <Button
              size="sm"
              disabled={text.trim() === ''}
              loading={mutations.comment.isPending}
              onClick={() =>
                mutations.comment.mutate(text, { onSuccess: () => setText('') })
              }
            >
              Comentar
            </Button>
          </div>
        </div>
      )}
      {!items ? (
        <p className="text-sm text-texto-suave">Carregando a atividade…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-texto-suave">Nada registrado ainda.</p>
      ) : (
        <ol className="flex flex-col gap-3">
          {items.map((item) =>
            item.kind === 'comentario' ? (
              <li
                key={item.key}
                className="rounded-borda border border-borda bg-superficie px-3 py-2.5 text-sm"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-semibold">
                    {item.comment.authorName ?? 'Alguém'}
                  </span>
                  <span className="text-xs text-texto-suave tabular-nums">
                    {formatInstant(item.at)}
                    {item.comment.editedAt ? ' · editado' : ''}
                  </span>
                </div>
                {item.comment.archivedAt ? (
                  <p className="mt-1 text-texto-suave italic">Comentário removido.</p>
                ) : editing?.id === item.comment.id ? (
                  <div className="mt-2 flex flex-col gap-2">
                    <TextArea
                      label="Corrigir o comentário"
                      value={editing.text}
                      onChange={(ev) =>
                        setEditing({ id: editing.id, text: ev.target.value })
                      }
                      maxLength={4000}
                      rows={2}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="secundario"
                        onClick={() => setEditing(null)}
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        disabled={editing.text.trim() === ''}
                        loading={mutations.updateComment.isPending}
                        onClick={() =>
                          mutations.updateComment.mutate(
                            { commentId: editing.id, change: { body: editing.text } },
                            { onSuccess: () => setEditing(null) },
                          )
                        }
                      >
                        Salvar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="mt-1 whitespace-pre-wrap">{item.comment.body}</p>
                    {canComment && item.comment.authorId === me && (
                      <div className="mt-1.5 flex gap-1">
                        <Button
                          size="sm"
                          variant="discreto"
                          onClick={() =>
                            setEditing({ id: item.comment.id, text: item.comment.body })
                          }
                        >
                          Corrigir
                        </Button>
                        <Button
                          size="sm"
                          variant="discreto"
                          onClick={() =>
                            mutations.updateComment.mutate({
                              commentId: item.comment.id,
                              change: { archived: true },
                            })
                          }
                        >
                          Remover
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </li>
            ) : (
              <li key={item.key} className="flex flex-col px-1 text-sm">
                <span>
                  <span className="font-semibold">{item.summary}</span>
                  {item.detail ? (
                    <span className="text-texto-suave"> · {item.detail}</span>
                  ) : null}
                </span>
                <span className="text-xs text-texto-suave tabular-nums">
                  {formatInstant(item.at)} · {item.actorName ?? 'o sistema'}
                </span>
              </li>
            ),
          )}
        </ol>
      )}
    </div>
  );
}

/** O nome de cada documento da despesa, na grade da aba Anexos. */
// @valor
const SLOT_LABELS: Readonly<
  Record<Exclude<FinanceExpenseFile['slot'], 'anexo'>, string>
> = {
  documento: 'Documento a pagar',
  nota_fiscal: 'NF-e',
  comprovante: 'Comprovante de pagamento',
};

/**
 * Os Anexos (refeitos em 28/10/2026, pedido do Paulo): os documentos da
 * despesa, só para ver, e os anexos GERAIS do Financeiro — sem dizer o que
 * cada um é, porque a NF-e e o comprovante têm o lugar deles no Formulário.
 * A grade é a da casa (`AttachmentField`/`AttachmentGallery`), com prévia;
 * tirar um anexo geral é arquivá-lo.
 */
function FilesTab({
  expenseId,
  files,
  error,
  canAttach,
  mutations,
}: {
  expenseId: number;
  files: FinanceExpenseFile[] | undefined;
  error: Error | null;
  canAttach: boolean;
  mutations: Mutations;
}) {
  const [sending, setSending] = useState(false);
  if (error) return <Alert tone="erro">{error.message}</Alert>;
  if (!files) return <p className="text-sm text-texto-suave">Carregando os anexos…</p>;
  const own = files.filter((f) => f.slot !== 'anexo');
  const general = files.filter((f) => f.slot === 'anexo');
  const generalItems = general.map((f) => fileItem(expenseId, f));

  const send = async (picked: File[]) => {
    setSending(true);
    try {
      /* Um de cada vez: o envio parcial diz qual arquivo não foi. */
      for (const file of picked)
        await mutations.attach.mutateAsync({ file, kind: 'outro' });
    } catch {
      /* a mensagem fica no `mutations.attach.error`, abaixo */
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <AttachmentGallery
        label="Documentos da despesa"
        items={own.map((f) =>
          fileItem(expenseId, f, SLOT_LABELS[f.slot as keyof typeof SLOT_LABELS]),
        )}
        empty={<p className="text-sm text-texto-suave">Nenhum documento da despesa.</p>}
      />
      {canAttach ? (
        <AttachmentField
          label="Anexos gerais"
          items={generalItems}
          max={50}
          busy={sending}
          disabled={sending}
          onAdd={(picked) => void send(picked)}
          onRemove={(key) => mutations.archiveFile.mutate(Number(key))}
        />
      ) : (
        <AttachmentGallery
          label="Anexos gerais"
          items={generalItems}
          empty={<p className="text-sm text-texto-suave">Nenhum anexo geral.</p>}
        />
      )}
      {(mutations.attach.isError || mutations.archiveFile.isError) && (
        <Alert tone="erro">
          {(mutations.attach.error ?? mutations.archiveFile.error)?.message}
        </Alert>
      )}
    </div>
  );
}

/**
 * Os atos do Financeiro no rodapé da mesa (28/10/2026, pedido do Paulo): são
 * poucos — autorizar e recusar na Aprovação Financeiro, devolver em Aguardando
 * Pagamento —, e uma coluna inteira para dois botões era espaço perdido. Lado a
 * lado, alinhados à direita, com o principal na ponta. A Pendência está aqui
 * também (pedido do Paulo, 28/10/2026), e abre o mesmo diálogo do registro da
 * Atividade.
 */
function DeskActions({
  expense: e,
  error,
  running,
  onRun,
  onRefuse,
  onHold,
}: {
  expense: FinanceExpenseDetail;
  error: string | null;
  running: ExpenseFinanceAct | null;
  onRun: (act: ExpenseFinanceAct) => void;
  onRefuse: () => void;
  onHold: () => void;
}) {
  const busy = running !== null;
  return (
    <>
      {error && <p className="mr-auto min-w-0 text-sm text-erro">{error}</p>}
      {e.acts.includes('devolver') && (
        <Button
          variant="secundario"
          icon="seta-esquerda"
          loading={running === 'devolver'}
          disabled={busy}
          onClick={() => onRun('devolver')}
        >
          {EXPENSE_FINANCE_ACT_LABELS.devolver}
        </Button>
      )}
      {e.acts.includes('pendenciar') && (
        <Button variant="secundario" icon="pausa" disabled={busy} onClick={onHold}>
          Pendência
        </Button>
      )}
      {e.acts.includes('recusar') && (
        <Button variant="perigo" disabled={busy} onClick={onRefuse}>
          {EXPENSE_FINANCE_ACT_LABELS.recusar}
        </Button>
      )}
      {e.acts.includes('autorizar') && (
        <Button
          icon="confirmado"
          loading={running === 'autorizar'}
          disabled={busy}
          onClick={() => onRun('autorizar')}
        >
          {EXPENSE_FINANCE_ACT_LABELS.autorizar}
        </Button>
      )}
    </>
  );
}

/** Recusar pede motivo, e é final: o diálogo diz isso antes do clique. */
function RefuseDialog({
  busy,
  onCancel,
  onConfirm,
}: {
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => Promise<unknown>;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onCancel()}
      title="Recusar a despesa"
      descricao="A despesa vai para Pagamento Recusado, e a recusa é final."
      icon="fechar"
      size="sm"
    >
      <div className="flex flex-col gap-4">
        <TextArea
          label="Motivo da recusa"
          required
          value={reason}
          onChange={(ev) => setReason(ev.target.value)}
          maxLength={2000}
          rows={3}
        />
        {error && <Alert tone="erro">{error}</Alert>}
        <ModalActions>
          <Button variant="secundario" disabled={busy} onClick={onCancel}>
            Cancelar
          </Button>
          <Button
            variant="perigo"
            loading={busy}
            disabled={reason.trim() === ''}
            onClick={() => {
              setError(null);
              onConfirm(reason.trim()).catch((err: Error) => setError(err.message));
            }}
          >
            Recusar
          </Button>
        </ModalActions>
      </div>
    </Modal>
  );
}
